import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import api from '../../api';
import Swal from 'sweetalert2';
import Select from 'react-select';
import QRCode from 'react-qr-code';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import QRCodeLib from 'qrcode';
import { 
  FaSearch, 
  FaPlus, 
  FaEdit, 
  FaToggleOn, 
  FaToggleOff, 
  FaBox, 
  FaCube, 
  FaExclamationTriangle, 
  FaBan,
  FaUpload,
  FaSave,
  FaTimes,
  FaEye,
  FaWarehouse,
  FaFilter,
  FaDownload,
  FaSync,
  FaQrcode,
  FaFilePdf,
  FaFileImage,
  FaTh,
  FaList,
  FaFileExport,
  FaTruck,
  FaWeight,
  FaCheck,
  FaCheckSquare,
  FaSquare,
  FaCalendarAlt,
  FaFileExcel
} from 'react-icons/fa';

const TilesManagement = () => {
  const { user } = useContext(AuthContext);
  
  // Dark mode from localStorage
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTile, setSelectedTile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [statistics, setStatistics] = useState({});
  const [locations, setLocations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [sizesMM, setSizesMM] = useState([]);
  const [sizesInches, setSizesInches] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    category: null,
    type: null,
    brand: null,
    status: null
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 20;

  // Stock editing
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [stockValues, setStockValues] = useState({
    freshStockBox: 0,
    freshStockPieces: 0,
    damagedStockBox: 0,
    damagedStockPieces: 0
  });

  // QR Code modal states
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrTile, setQrTile] = useState(null);

  // View mode and selection states (production features)
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedTiles, setSelectedTiles] = useState([]);
  const [showBulkQRModal, setShowBulkQRModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [finishes, setFinishes] = useState([]);
  const [bulkQRFilters, setBulkQRFilters] = useState({
    brand: null,
    category: null,
    type: null,
    finish: null,
    sizeMM: null,
    sizeInches: null,
    status: 'active'
  });
  const [bulkQRTiles, setBulkQRTiles] = useState([]);
  const [loadingBulkQR, setLoadingBulkQR] = useState(false);

  // Export Stock modal states
  const [showExportStockModal, setShowExportStockModal] = useState(false);
  const [exportStockFilters, setExportStockFilters] = useState({
    brand: null,
    category: null,
    type: null,
    finish: null,
    sizeMM: null,
    sizeInches: null,
    status: 'active',
    location: null
  });

  // Stock Movement Export modal states
  const [showStockMovementModal, setShowStockMovementModal] = useState(false);
  const [stockMovementLoading, setStockMovementLoading] = useState(false);
  const [stockMovementFilters, setStockMovementFilters] = useState(() => {
    const now = new Date();
    const fyYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    return {
      startDate: `${fyYear}-04-01`,
      endDate: now.toISOString().split('T')[0],
      location: null,
      brand: null,
      category: null,
      type: null,
      finish: null,
      sizeMM: null,
      sizeInches: null
    };
  });

  // Bulk Weight Update modal states
  const [showBulkWeightModal, setShowBulkWeightModal] = useState(false);
  const [bulkWeightLoading, setBulkWeightLoading] = useState(false);
  const [bulkWeightTiles, setBulkWeightTiles] = useState([]);
  const [bulkWeightFilters, setBulkWeightFilters] = useState({
    brand: null,
    category: null,
    type: null,
    sizeMM: null
  });
  const [bulkWeightValues, setBulkWeightValues] = useState({});

  // Edit mode states
  const [isEditingTile, setIsEditingTile] = useState(false);
  const [editFormData, setEditFormData] = useState({
    tilesName: '',
    partNumber: '',
    displayName: '',
    tilesCategory: '',
    tilesType: '',
    tilesMM: '',
    tilesInches: '',
    brand: '',
    status: 'active'
  });

  // Helper to safely extract company ID from user context (company may be populated object or string)
  const getCompanyId = () => {
    const company = user?.user?.company;
    if (!company) return null;
    if (typeof company === 'string') return company;
    return company._id || company.id || null;
  };

  useEffect(() => {
    const companyId = getCompanyId();
    if (!companyId) return;
    fetchTiles();
    fetchStatistics();
    fetchFilterOptions();
  }, [currentPage, searchTerm, filters, user]);

  const fetchTiles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        companyId: getCompanyId(),
        page: currentPage,
        limit: itemsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.category && { category: filters.category }),
        ...(filters.type && { type: filters.type }),
        ...(filters.brand && { brand: filters.brand }),
        ...(filters.status && { status: filters.status })
      });

      const response = await api.get(`/tiles/management/all?${params}`);
      
      if (response.data.success) {
        setTiles(response.data.data.tiles);
        setTotalPages(response.data.data.pagination.total);
        setTotalRecords(response.data.data.pagination.totalRecords);
      }
    } catch (error) {
      console.error('Error fetching tiles:', error);
      Swal.fire('Error', 'Failed to fetch tiles', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get(`/tiles/management/statistics/${getCompanyId()}`);
      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [locationsRes, brandsRes, parametersRes] = await Promise.all([
        api.get(`/locations/companyId/${getCompanyId()}`),
        api.get('/brands'),
        api.get('/tiles/parameters?limit=2000').catch(err => {
          console.error('Tiles parameters API error:', err);
          // Fallback: try without query params
          return api.get('/tiles/parameters');
        })
      ]);

      // Handle different API response structures
      // Locations API returns data directly, brands API returns data wrapped in data property
      const locationsData = locationsRes.data || [];
      const brandsData = brandsRes.data?.data || brandsRes.data || [];
      
      console.log('Company-specific locations loaded:', {
        companyId: getCompanyId(),
        locationsCount: locationsData.length,
        locations: locationsData.map(loc => ({ id: loc._id, name: loc.name || loc.locationName, company: loc.company }))
      });
      
      setLocations(locationsData);
      setBrands(brandsData);
      
      // Process parameters by assignedFor - handle different response structures
      const parameters = parametersRes.data?.data?.parameters || parametersRes.data?.parameters || [];
      console.log('Raw parameters API response:', {
        success: parametersRes.data?.success,
        dataExists: !!parametersRes.data?.data,
        parametersExists: !!parametersRes.data?.parameters,
        dataParametersLength: parametersRes.data?.data?.parameters?.length,
        rootParametersLength: parametersRes.data?.parameters?.length,
        fullResponse: parametersRes.data
      });
      console.log('Total parameters found:', parameters.length);
      
      if (parameters.length > 0) {
        console.log('Sample parameters:', parameters.slice(0, 3));
        
        // Check what assignedFor values we have
        const assignedForValues = [...new Set(parameters.map(p => p.assignedFor))];
        console.log('Available assignedFor values:', assignedForValues);
        
        const categoriesData = parameters.filter(p => p.assignedFor === 'category');
        const typesData = parameters.filter(p => p.assignedFor === 'type');
        const sizeMMData = parameters.filter(p => p.assignedFor === 'mm');
        const sizeInchesData = parameters.filter(p => p.assignedFor === 'inches');
        const finishesData = parameters.filter(p => p.assignedFor === 'finish');
        
        setCategories(categoriesData);
        setTypes(typesData);
        setSizesMM(sizeMMData);
        setSizesInches(sizeInchesData);
        setFinishes(finishesData);
        
        console.log('Filter options loaded:', {
          companyId: getCompanyId(),
          categories: categoriesData.length,
          types: typesData.length,
          sizesMM: sizeMMData.length,
          sizesInches: sizeInchesData.length,
          finishes: finishesData.length,
          locations: locationsData.length,
          brands: brandsData.length
        });
      } else {
        console.warn('No parameters found in API response');
        // Set empty arrays
        setCategories([]);
        setTypes([]);
        setSizesMM([]);
        setSizesInches([]);
        setFinishes([]);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Set empty arrays on error
      setCategories([]);
      setTypes([]);
      setSizesMM([]);
      setSizesInches([]);
      setFinishes([]);
    }
  };

  const handleStockEdit = (tile, location) => {
    setSelectedTile(tile);
    setSelectedLocation(location);
    
    // Find existing stock for this location
    const existingStock = tile.stock?.find(s => s.location._id === location._id);
    if (existingStock) {
      setStockValues({
        freshStockBox: existingStock.freshStockBox || 0,
        freshStockPieces: existingStock.freshStockPieces || 0,
        damagedStockBox: existingStock.damagedStockBox || 0,
        damagedStockPieces: existingStock.damagedStockPieces || 0
      });
    } else {
      setStockValues({
        freshStockBox: 0,
        freshStockPieces: 0,
        damagedStockBox: 0,
        damagedStockPieces: 0
      });
    }
    
    setShowStockModal(true);
  };

  const handleStockUpdate = async () => {
    try {
      const response = await api.put(
        `/tiles/management/${selectedTile._id}/stock/${selectedLocation._id}`,
        {
          ...stockValues,
          companyId: getCompanyId(),
          userId: user?.user?._id,
          reason: 'Manual stock adjustment by company head'
        }
      );

      if (response.data.success) {
        Swal.fire('Success', 'Stock updated successfully', 'success');
        setShowStockModal(false);
        fetchTiles();
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error updating stock:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to update stock', 'error');
    }
  };

  // Tile editing handlers
  const handleEditTile = async () => {
    // Ensure filter options are loaded before editing
    if (categories.length === 0 || types.length === 0) {
      await fetchFilterOptions();
    }
    
    console.log('Edit tile data:', selectedTile);
    console.log('Available options:', { 
      categories: categories.length, 
      types: types.length, 
      brands: brands.length,
      sizesMM: sizesMM.length,
      sizesInches: sizesInches.length
    });
    
    setEditFormData({
      tilesName: selectedTile.tilesName || '',
      partNumber: selectedTile.partNumber || '',
      displayName: selectedTile.displayName || '',
      tilesCategory: selectedTile.tilesCategory?._id || '',
      tilesType: selectedTile.tilesType?._id || '',
      tilesMM: selectedTile.tilesMM?._id || '',
      tilesInches: selectedTile.tilesInches?._id || '',
      brand: selectedTile.brand?._id || '',
      status: selectedTile.status || 'active'
    });
    setIsEditingTile(true);
  };

  const handleCancelEdit = () => {
    setIsEditingTile(false);
    setEditFormData({
      tilesName: '',
      partNumber: '',
      displayName: '',
      tilesCategory: '',
      tilesType: '',
      tilesMM: '',
      tilesInches: '',
      brand: '',
      status: 'active'
    });
  };

  const handleSaveTile = async () => {
    try {
      const response = await api.put(
        `/tiles/management/${selectedTile._id}`,
        {
          ...editFormData,
          companyId: getCompanyId(),
          userId: user?.user?._id
        }
      );

      if (response.data.success) {
        Swal.fire('Success', 'Tile updated successfully', 'success');
        setIsEditingTile(false);
        setSelectedTile(response.data.data); // Update selected tile with new data
        fetchTiles(); // Refresh the tiles list
        fetchStatistics();
      }
    } catch (error) {
      console.error('Error updating tile:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to update tile', 'error');
    }
  };

  const handleToggleTileStatus = async (tile) => {
    const newStatus = tile.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/tiles/update-status/${tile._id}`, { status: newStatus });
      setTiles(prev => prev.map(t => t._id === tile._id ? { ...t, status: newStatus } : t));
      fetchStatistics();
      Swal.fire({
        icon: 'success',
        title: newStatus === 'active' ? 'Tile Enabled' : 'Tile Disabled',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000
      });
    } catch (error) {
      console.error('Error updating tile status:', error);
      Swal.fire('Error', 'Failed to update tile status', 'error');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      category: null,
      type: null,
      brand: null,
      status: null
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const getTotalStock = (tile) => {
    return tile.stock?.reduce((acc, stock) => ({
      freshBoxes: acc.freshBoxes + (stock.freshStockBox || 0),
      freshPieces: acc.freshPieces + (stock.freshStockPieces || 0),
      damagedBoxes: acc.damagedBoxes + (stock.damagedStockBox || 0),
      damagedPieces: acc.damagedPieces + (stock.damagedStockPieces || 0)
    }), { freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0 }) || 
    { freshBoxes: 0, freshPieces: 0, damagedBoxes: 0, damagedPieces: 0 };
  };

  // QR Code handlers
  const handleShowQR = (tile) => {
    setQrTile(tile);
    setShowQRModal(true);
  };

  const getQRSvgElement = () => {
    return document.getElementById('tile-qr-code-svg');
  };

  const svgToCanvas = (svg, callback) => {
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width * 2; // Higher resolution
      canvas.height = img.height * 2;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      callback(canvas);
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const downloadQRAsPNG = () => {
    const svg = getQRSvgElement();
    if (!svg) return;

    svgToCanvas(svg, (canvas) => {
      const pngDataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${qrTile.tilesName || qrTile.id}_QR.png`;
      link.href = pngDataUrl;
      link.click();
    });
  };

  const downloadQRAsJPG = () => {
    const svg = getQRSvgElement();
    if (!svg) return;

    svgToCanvas(svg, (canvas) => {
      const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.95);
      const link = document.createElement('a');
      link.download = `${qrTile.tilesName || qrTile.id}_QR.jpg`;
      link.href = jpgDataUrl;
      link.click();
    });
  };

  const downloadQRAsSVG = () => {
    const svg = getQRSvgElement();
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `${qrTile.tilesName || qrTile.id}_QR.svg`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadQRAsPDF = (includeDetails = false) => {
    const svg = getQRSvgElement();
    if (!svg) return;

    svgToCanvas(svg, (canvas) => {
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF with size 45mm x 65mm (portrait orientation)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [45, 65]
      });

      if (includeDetails) {
        // Add tile name at top
        const tileName = qrTile.tilesName || 'Tile';
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        const nameWidth = pdf.getTextWidth(tileName);
        const nameX = (45 - nameWidth) / 2;
        pdf.text(tileName, nameX > 0 ? nameX : 2, 8);

        // Add QR code below name (centered)
        const qrSize = 25;
        const qrX = (45 - qrSize) / 2;
        pdf.addImage(imgData, 'PNG', qrX, 12, qrSize, qrSize);

        // Add tile ID below QR
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        const idText = `ID: ${qrTile.id}`;
        const idWidth = pdf.getTextWidth(idText);
        pdf.text(idText, (45 - idWidth) / 2, 42);

        // Add size info if available
        if (qrTile.tilesMM?.parameterName) {
          const sizeText = qrTile.tilesMM.parameterName;
          const sizeWidth = pdf.getTextWidth(sizeText);
          pdf.text(sizeText, (45 - sizeWidth) / 2, 47);
        }

        // Add category if available
        if (qrTile.tilesCategory?.parameterName) {
          const catText = qrTile.tilesCategory.parameterName;
          const catWidth = pdf.getTextWidth(catText);
          pdf.text(catText, (45 - catWidth) / 2, 52);
        }

        pdf.save(`${qrTile.tilesName || qrTile.id}_QR_with_details.pdf`);
      } else {
        // QR only - centered
        const qrSize = 35;
        const qrX = (45 - qrSize) / 2;
        pdf.addImage(imgData, 'PNG', qrX, 15, qrSize, qrSize);
        pdf.save(`${qrTile.tilesName || qrTile.id}_QR.pdf`);
      }
    });
  };

  const StatusBadge = ({ status }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
      status === 'active' 
        ? darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
        : darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
    }`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );

  // Selection handling functions
  const handleSelectAll = () => {
    if (selectedTiles.length === tiles.length) {
      setSelectedTiles([]);
    } else {
      setSelectedTiles(tiles.map(t => t._id));
    }
  };

  const handleSelectTile = (tileId) => {
    setSelectedTiles(prev => {
      if (prev.includes(tileId)) {
        return prev.filter(id => id !== tileId);
      } else {
        return [...prev, tileId];
      }
    });
  };

  // Export Stock to Excel function - with filters
  const handleExportStock = async () => {
    try {
      setExportLoading(true);
      
      const params = new URLSearchParams({
        userId: user?.user?._id,
        ...(exportStockFilters.brand && { brand: exportStockFilters.brand }),
        ...(exportStockFilters.category && { category: exportStockFilters.category }),
        ...(exportStockFilters.type && { type: exportStockFilters.type }),
        ...(exportStockFilters.finish && { finish: exportStockFilters.finish }),
        ...(exportStockFilters.sizeMM && { tilesMM: exportStockFilters.sizeMM }),
        ...(exportStockFilters.sizeInches && { tilesInches: exportStockFilters.sizeInches }),
        ...(exportStockFilters.status && { status: exportStockFilters.status }),
        ...(exportStockFilters.location && { location: exportStockFilters.location })
      });
      
      const response = await api.get(`/tiles/export-all-excel?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tiles_stock_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      Swal.fire('Success', 'Stock data exported successfully', 'success');
      setShowExportStockModal(false);
    } catch (error) {
      console.error('Error exporting stock:', error);
      Swal.fire('Error', 'Failed to export stock data', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle export stock filter change
  const handleExportStockFilterChange = (field, value) => {
    setExportStockFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch tiles for bulk QR export based on filters
  const fetchBulkQRTiles = async () => {
    try {
      setLoadingBulkQR(true);
      const params = new URLSearchParams({
        companyId: getCompanyId(),
        limit: 5000, // Get all matching tiles
        ...(bulkQRFilters.brand && { brand: bulkQRFilters.brand }),
        ...(bulkQRFilters.category && { category: bulkQRFilters.category }),
        ...(bulkQRFilters.type && { type: bulkQRFilters.type }),
        ...(bulkQRFilters.finish && { finish: bulkQRFilters.finish }),
        ...(bulkQRFilters.sizeMM && { tilesMM: bulkQRFilters.sizeMM }),
        ...(bulkQRFilters.sizeInches && { tilesInches: bulkQRFilters.sizeInches }),
        ...(bulkQRFilters.status && { status: bulkQRFilters.status })
      });

      const response = await api.get(`/tiles/management/all?${params}`);
      
      if (response.data.success) {
        setBulkQRTiles(response.data.data.tiles);
      }
    } catch (error) {
      console.error('Error fetching tiles for bulk QR:', error);
      Swal.fire('Error', 'Failed to fetch tiles', 'error');
    } finally {
      setLoadingBulkQR(false);
    }
  };

  // Generate bulk QR codes as ZIP with folders
  const generateBulkQRZip = async () => {
    if (bulkQRTiles.length === 0) {
      Swal.fire('Warning', 'No tiles found with the selected filters', 'warning');
      return;
    }

    try {
      setExportLoading(true);
      
      Swal.fire({
        title: 'Generating QR Codes',
        html: 'Creating QR codes for <b>0</b> / <b>' + bulkQRTiles.length + '</b> tiles...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const zip = new JSZip();

      for (let i = 0; i < bulkQRTiles.length; i++) {
        const tile = bulkQRTiles[i];
        
        // Update progress
        Swal.update({
          html: `Creating QR codes for <b>${i + 1}</b> / <b>${bulkQRTiles.length}</b> tiles...`
        });

        // Create folder name: "TileName_ID"
        const folderName = `${tile.tilesName.replace(/[/\\?%*:|"<>]/g, '-')}_${tile.id}`;
        const tileFolder = zip.folder(folderName);

        // QR code data - use ObjectId like TilesStockStatus
        const qrValue = tile._id;

        // 1. Generate PNG (high quality)
        try {
          const pngDataUrl = await QRCodeLib.toDataURL(qrValue, {
            width: 512,
            margin: 2,
            errorCorrectionLevel: 'H',
            type: 'image/png'
          });
          const pngBase64 = pngDataUrl.split(',')[1];
          tileFolder.file(`${tile.id}_QR.png`, pngBase64, { base64: true });
        } catch (err) {
          console.error('Error generating PNG for', tile.id, err);
        }

        // 2. Generate JPG
        try {
          const jpgDataUrl = await QRCodeLib.toDataURL(qrValue, {
            width: 512,
            margin: 2,
            errorCorrectionLevel: 'H',
            type: 'image/jpeg',
            quality: 0.95
          });
          const jpgBase64 = jpgDataUrl.split(',')[1];
          tileFolder.file(`${tile.id}_QR.jpg`, jpgBase64, { base64: true });
        } catch (err) {
          console.error('Error generating JPG for', tile.id, err);
        }

       // 3. Generate SVG
        try {
          const svgString = await QRCodeLib.toString(qrValue, {
            type: 'svg',
            width: 512,
            margin: 2,
            errorCorrectionLevel: 'H'
          });
          tileFolder.file(`${tile.id}_QR.svg`, svgString);
        } catch (err) {
          console.error('Error generating SVG for', tile.id, err);
        }

        // 4. Generate PDF (QR only)
        try {
          const pdfQROnly = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [45, 65]
          });
          
          const qrDataUrl = await QRCodeLib.toDataURL(qrValue, {
            width: 300,
            margin: 1,
            errorCorrectionLevel: 'M'
          });
          
          const qrSize = 35;
          const qrX = (45 - qrSize) / 2;
          pdfQROnly.addImage(qrDataUrl, 'PNG', qrX, 15, qrSize, qrSize);
          
          const pdfBlob = pdfQROnly.output('blob');
          tileFolder.file(`${tile.id}_QR_Only.pdf`, pdfBlob);
        } catch (err) {
          console.error('Error generating PDF (QR only) for', tile.id, err);
        }

        // 5. Generate PDF with details
        try {
          const pdfWithDetails = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: [45, 65]
          });
          
          const qrDataUrl = await QRCodeLib.toDataURL(qrValue, {
            width: 300,
            margin: 1,
            errorCorrectionLevel: 'M'
          });

          // Add tile name at top
          const tileName = tile.tilesName || 'Tile';
          pdfWithDetails.setFontSize(10);
          pdfWithDetails.setFont('helvetica', 'bold');
          const nameWidth = pdfWithDetails.getTextWidth(tileName);
          const nameX = (45 - nameWidth) / 2;
          pdfWithDetails.text(tileName, nameX > 0 ? nameX : 2, 8);

          // Add QR code
          const qrSize = 25;
          const qrX = (45 - qrSize) / 2;
          pdfWithDetails.addImage(qrDataUrl, 'PNG', qrX, 12, qrSize, qrSize);

          // Add tile ID
          pdfWithDetails.setFontSize(8);
          pdfWithDetails.setFont('helvetica', 'normal');
          const idText = `ID: ${tile.id}`;
          const idWidth = pdfWithDetails.getTextWidth(idText);
          pdfWithDetails.text(idText, (45 - idWidth) / 2, 42);

          // Add size if available
          if (tile.tilesMM?.parameterName) {
            const sizeText = tile.tilesMM.parameterName;
            const sizeWidth = pdfWithDetails.getTextWidth(sizeText);
            pdfWithDetails.text(sizeText, (45 - sizeWidth) / 2, 47);
          }

          // Add category if available
          if (tile.tilesCategory?.parameterName) {
            const catText = tile.tilesCategory.parameterName;
            const catWidth = pdfWithDetails.getTextWidth(catText);
            pdfWithDetails.text(catText, (45 - catWidth) / 2, 52);
          }

          const pdfBlob = pdfWithDetails.output('blob');
          tileFolder.file(`${tile.id}_QR_Details.pdf`, pdfBlob);
        } catch (err) {
          console.error('Error generating PDF (with details) for', tile.id, err);
        }

        // Add a small delay to prevent browser freezing
        if (i % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }

      // Generate and download ZIP
      Swal.update({
        html: 'Compressing files...'
      });

      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      saveAs(zipBlob, `QR_Codes_${new Date().toISOString().split('T')[0]}.zip`);

      await Swal.fire({
        title: 'Success!',
        text: `Generated QR codes for ${bulkQRTiles.length} tiles`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false
      });

      setShowBulkQRModal(false);
    } catch (error) {
      console.error('Error generating bulk QR ZIP:', error);
      Swal.fire('Error', 'Failed to generate QR codes: ' + error.message, 'error');
    } finally {
      setExportLoading(false);
    }
  };

  // Handle bulk QR filter change
  const handleBulkQRFilterChange = (field, value) => {
    setBulkQRFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Effect to fetch tiles when bulk QR filters change
  useEffect(() => {
    if (showBulkQRModal) {
      fetchBulkQRTiles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkQRFilters, showBulkQRModal]);

  // Stock Movement Export handler
  const handleStockMovementExport = async () => {
    if (!stockMovementFilters.location) {
      Swal.fire('Warning', 'Please select a location', 'warning');
      return;
    }
    try {
      setStockMovementLoading(true);
      const params = new URLSearchParams({
        userId: user?.user?._id,
        startDate: stockMovementFilters.startDate,
        endDate: stockMovementFilters.endDate,
        location: stockMovementFilters.location,
        ...(stockMovementFilters.brand && { brand: stockMovementFilters.brand }),
        ...(stockMovementFilters.category && { category: stockMovementFilters.category }),
        ...(stockMovementFilters.type && { type: stockMovementFilters.type }),
        ...(stockMovementFilters.finish && { finish: stockMovementFilters.finish }),
        ...(stockMovementFilters.sizeMM && { tilesMM: stockMovementFilters.sizeMM }),
        ...(stockMovementFilters.sizeInches && { tilesInches: stockMovementFilters.sizeInches })
      });
      const response = await api.get(`/tiles/export-stock-movement?${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Stock_Movement_${stockMovementFilters.startDate}_to_${stockMovementFilters.endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      Swal.fire('Success', 'Stock movement exported successfully', 'success');
      setShowStockMovementModal(false);
    } catch (error) {
      console.error('Error exporting stock movement:', error);
      Swal.fire('Error', 'Failed to export stock movement data', 'error');
    } finally {
      setStockMovementLoading(false);
    }
  };

  // Fetch tiles for bulk weight update
  const fetchBulkWeightTiles = async () => {
    try {
      setBulkWeightLoading(true);
      const params = new URLSearchParams({
        companyId: getCompanyId(),
        limit: 5000,
        ...(bulkWeightFilters.brand && { brand: bulkWeightFilters.brand }),
        ...(bulkWeightFilters.category && { category: bulkWeightFilters.category }),
        ...(bulkWeightFilters.type && { type: bulkWeightFilters.type }),
        ...(bulkWeightFilters.sizeMM && { tilesMM: bulkWeightFilters.sizeMM })
      });
      const response = await api.get(`/tiles/management/all?${params}`);
      if (response.data.success) {
        const fetchedTiles = response.data.data.tiles;
        setBulkWeightTiles(fetchedTiles);
        // Pre-populate existing weights
        const existingWeights = {};
        fetchedTiles.forEach(t => {
          if (t.weight) existingWeights[t._id] = t.weight;
        });
        setBulkWeightValues(existingWeights);
      }
    } catch (error) {
      console.error('Error fetching tiles for bulk weight:', error);
    } finally {
      setBulkWeightLoading(false);
    }
  };

  // Effect to fetch tiles when bulk weight filters change
  useEffect(() => {
    if (showBulkWeightModal) {
      fetchBulkWeightTiles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkWeightFilters, showBulkWeightModal]);

  // Handle bulk weight save
  const handleBulkWeightSave = async () => {
    try {
      const updates = Object.entries(bulkWeightValues)
        .filter(([, weight]) => weight !== '' && weight !== null && weight !== undefined)
        .map(([tileId, weight]) => ({
          tileId,
          weight: parseFloat(weight)
        }));
      if (updates.length === 0) {
        Swal.fire('Warning', 'No weight values to update', 'warning');
        return;
      }
      setBulkWeightLoading(true);
      const response = await api.put('/tiles/management/bulk-weight-update', {
        updates,
        userId: user?.user?._id
      });
      if (response.data.success) {
        Swal.fire('Success', `Updated weight for ${updates.length} tiles`, 'success');
        setShowBulkWeightModal(false);
        fetchTiles();
      }
    } catch (error) {
      console.error('Error saving bulk weights:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to update weights', 'error');
    } finally {
      setBulkWeightLoading(false);
    }
  };

  return (
    <div className={`p-6 min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className={`text-3xl font-bold flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <FaWarehouse className="mr-3 text-blue-600" />
            Tiles Management
          </h1>
          <div className="flex items-center space-x-2">
            {/* View Mode Toggle */}
            <div className={`flex border rounded-lg overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 flex items-center space-x-1 ${viewMode === 'cards' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100')}`}
                title="Cards View"
              >
                <FaTh className="w-4 h-4" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 flex items-center space-x-1 ${viewMode === 'table' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100')}`}
                title="Table View"
              >
                <FaList className="w-4 h-4" />
                <span className="hidden sm:inline">Table</span>
              </button>
            </div>

            {/* Export Stock Button */}
            <button
              onClick={() => setShowExportStockModal(true)}
              disabled={exportLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50"
              title="Export Stock"
            >
              <FaFileExport className="w-4 h-4" />
              <span className="hidden sm:inline">Export Stock</span>
            </button>

            {/* Export QR Codes Button */}
            <button
              onClick={() => setShowBulkQRModal(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
              title="Export QR Codes"
            >
              <FaQrcode className="w-4 h-4" />
              <span className="hidden sm:inline">Export QR Codes</span>
            </button>

            {/* Stock Movement Button */}
            <button
              onClick={() => setShowStockMovementModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center space-x-2"
              title="Export Stock Movement"
            >
              <FaTruck className="w-4 h-4" />
              <span className="hidden sm:inline">Stock Movement</span>
            </button>

            {/* Bulk Weight Button */}
            <button
              onClick={() => setShowBulkWeightModal(true)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center space-x-2"
              title="Bulk Weight Update"
            >
              <FaWeight className="w-4 h-4" />
              <span className="hidden sm:inline">Bulk Weight</span>
            </button>

            {/* Refresh Button */}
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <FaSync className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className={`p-4 rounded-lg shadow border-l-4 border-blue-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Tiles</p>
                <p className="text-2xl font-bold text-blue-600">{statistics.totalTiles || 0}</p>
              </div>
              <FaCube className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className={`p-4 rounded-lg shadow border-l-4 border-green-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active</p>
                <p className="text-2xl font-bold text-green-600">{statistics.activeTiles || 0}</p>
              </div>
              <FaToggleOn className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className={`p-4 rounded-lg shadow border-l-4 border-red-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Inactive</p>
                <p className="text-2xl font-bold text-red-600">{statistics.inactiveTiles || 0}</p>
              </div>
              <FaToggleOff className="w-8 h-8 text-red-500" />
            </div>
          </div>

          <div className={`p-4 rounded-lg shadow border-l-4 border-orange-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>With Stock</p>
                <p className="text-2xl font-bold text-orange-600">{statistics.tilesWithStock || 0}</p>
              </div>
              <FaBox className="w-8 h-8 text-orange-500" />
            </div>
          </div>

          <div className={`p-4 rounded-lg shadow border-l-4 border-indigo-500 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fresh Boxes</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {statistics.stockSummary?.totalFreshBoxes || 0}
                </p>
              </div>
              <FaWarehouse className="w-8 h-8 text-indigo-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className={`p-4 rounded-lg shadow mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search tiles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
              />
              <FaSearch className={`absolute left-3 top-3 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <Select
              placeholder="Category"
              isClearable
              value={filters.category ? { value: filters.category, label: categories.find(c => c._id === filters.category)?.parameterName } : null}
              onChange={(option) => handleFilterChange('category', option?.value || null)}
              options={categories.map(cat => ({ value: cat._id, label: cat.parameterName }))}
              styles={darkMode ? {
                control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                singleValue: (base) => ({ ...base, color: '#fff' }),
                menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                input: (base) => ({ ...base, color: '#fff' }),
                placeholder: (base) => ({ ...base, color: '#9CA3AF' })
              } : {}}
            />
          </div>

          {/* Type Filter */}
          <div>
            <Select
              placeholder="Type"
              isClearable
              value={filters.type ? { value: filters.type, label: types.find(t => t._id === filters.type)?.parameterName } : null}
              onChange={(option) => handleFilterChange('type', option?.value || null)}
              options={types.map(type => ({ value: type._id, label: type.parameterName }))}
              styles={darkMode ? {
                control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                singleValue: (base) => ({ ...base, color: '#fff' }),
                menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                input: (base) => ({ ...base, color: '#fff' }),
                placeholder: (base) => ({ ...base, color: '#9CA3AF' })
              } : {}}
            />
          </div>

          {/* Brand Filter */}
          <div>
            <Select
              placeholder="Brand"
              isClearable
              value={filters.brand ? { value: filters.brand, label: brands.find(b => b._id === filters.brand)?.name } : null}
              onChange={(option) => handleFilterChange('brand', option?.value || null)}
              options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
              styles={darkMode ? {
                control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                singleValue: (base) => ({ ...base, color: '#fff' }),
                menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                input: (base) => ({ ...base, color: '#fff' }),
                placeholder: (base) => ({ ...base, color: '#9CA3AF' })
              } : {}}
            />
          </div>

          {/* Status Filter */}
          <div>
            <Select
              placeholder="Status"
              isClearable
              value={filters.status ? { value: filters.status, label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) } : null}
              onChange={(option) => handleFilterChange('status', option?.value || null)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              styles={darkMode ? {
                control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                singleValue: (base) => ({ ...base, color: '#fff' }),
                menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                input: (base) => ({ ...base, color: '#fff' }),
                placeholder: (base) => ({ ...base, color: '#9CA3AF' })
              } : {}}
            />
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={clearFilters}
            className={`px-4 py-2 flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
          >
            <FaTimes className="w-4 h-4" />
            <span>Clear Filters</span>
          </button>

          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {tiles.length} of {totalRecords} tiles
          </div>
        </div>
      </div>

      {/* Tiles Table */}
      {viewMode === 'table' && (
      <div className={`rounded-lg shadow overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-12 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  <button
                    onClick={handleSelectAll}
                    className={`p-1 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                    title={selectedTiles.length === tiles.length ? 'Deselect All' : 'Select All'}
                  >
                    {selectedTiles.length === tiles.length && tiles.length > 0 ? (
                      <FaCheckSquare className="w-5 h-5 text-blue-600" />
                    ) : (
                      <FaSquare className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Tile Details
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Specifications
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Status
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Weight
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Stock Summary
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-8">
                    <div className="inline-flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Loading tiles...</span>
                    </div>
                  </td>
                </tr>
              ) : tiles.length === 0 ? (
                <tr>
                  <td colSpan="7" className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No tiles found matching your criteria
                  </td>
                </tr>
              ) : (
                tiles.map((tile) => {
                  const totalStock = getTotalStock(tile);
                  return (
                    <tr key={tile._id} className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${selectedTiles.includes(tile._id) ? (darkMode ? 'bg-blue-900/30' : 'bg-blue-50') : ''}`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleSelectTile(tile._id)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          {selectedTiles.includes(tile._id) ? (
                            <FaCheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <FaSquare className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className={`text-sm font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                            {tile.tilesName}
                          </div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            ID: {tile.id} | Part: {tile.partNumber || 'N/A'}
                          </div>
                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Display: {tile.displayName || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          <div>{tile.tilesCategory?.parameterName} - {tile.tilesType?.parameterName}</div>
                          <div className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{tile.tilesMM?.parameterName} ({tile.tilesInches?.parameterName})</div>
                          <div className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{tile.brand?.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="space-y-2">
                          <StatusBadge status={tile.status} />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                          {tile.weight ? `${tile.weight} kg` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-xs space-y-1 ${darkMode ? 'text-gray-300' : ''}`}>
                          <div className="flex items-center space-x-2">
                            <FaBox className="w-3 h-3 text-green-600" />
                            <span>Fresh: {totalStock.freshBoxes}B, {totalStock.freshPieces}P</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <FaExclamationTriangle className="w-3 h-3 text-orange-600" />
                            <span>Damaged: {totalStock.damagedBoxes}B, {totalStock.damagedPieces}P</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={async () => {
                              setSelectedTile(tile);
                              // Ensure filter options are loaded before showing modal
                              if (categories.length === 0 || types.length === 0) {
                                await fetchFilterOptions();
                              }
                              setShowEditModal(true);
                            }}
                            className={`p-2 rounded ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`}
                            title="Manage Stock"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>

                          <button
                            onClick={async () => {
                              setSelectedTile(tile);
                              // Ensure filter options are loaded before showing modal
                              if (categories.length === 0 || types.length === 0) {
                                await fetchFilterOptions();
                              }
                              setShowEditModal(true);
                            }}
                            className={`p-2 rounded ${darkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-600 hover:bg-gray-100'}`}
                            title="View Details"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleShowQR(tile)}
                            className={`p-2 rounded ${darkMode ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-600 hover:bg-purple-100'}`}
                            title="QR Code"
                          >
                            <FaQrcode className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleToggleTileStatus(tile)}
                            className={`p-2 rounded ${tile.status === 'active' ? (darkMode ? 'text-green-400 hover:bg-green-900/30' : 'text-green-600 hover:bg-green-100') : (darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-100')}`}
                            title={tile.status === 'active' ? 'Disable Tile' : 'Enable Tile'}
                          >
                            {tile.status === 'active' ? <FaToggleOn className="w-4 h-4" /> : <FaToggleOff className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`px-4 py-3 flex items-center justify-between border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md disabled:opacity-50 ${darkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Tiles Cards View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full flex justify-center py-8">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Loading tiles...</span>
              </div>
            </div>
          ) : tiles.length === 0 ? (
            <div className={`col-span-full text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No tiles found matching your criteria
            </div>
          ) : (
            tiles.map((tile) => {
              const totalStock = getTotalStock(tile);
              return (
                <div key={tile._id} className={`rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow ${darkMode ? 'bg-gray-800' : 'bg-white'} ${selectedTiles.includes(tile._id) ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <button
                        onClick={() => handleSelectTile(tile._id)}
                        className={`p-1 rounded ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                      >
                        {selectedTiles.includes(tile._id) ? (
                          <FaCheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <FaSquare className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      <StatusBadge status={tile.status} />
                    </div>
                    
                    <h3 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{tile.tilesName}</h3>
                    <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {tile.id}</p>
                    
                    <div className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div>{tile.tilesCategory?.parameterName} - {tile.tilesType?.parameterName}</div>
                      <div>{tile.tilesMM?.parameterName}</div>
                      <div className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{tile.brand?.name}</div>
                    </div>
                    
                    <div className={`border-t pt-3 mt-3 ${darkMode ? 'border-gray-700' : ''}`}>
                      <div className="flex justify-between text-xs">
                        <span className="text-green-600">Fresh: {totalStock.freshBoxes}B, {totalStock.freshPieces}P</span>
                        <span className="text-orange-600">Damaged: {totalStock.damagedBoxes}B</span>
                      </div>
                      {tile.weight && (
                        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Weight: {tile.weight} kg</div>
                      )}
                    </div>
                    
                    <div className={`flex justify-center space-x-2 mt-3 border-t pt-3 ${darkMode ? 'border-gray-700' : ''}`}>
                      <button
                        onClick={async () => {
                          setSelectedTile(tile);
                          if (categories.length === 0 || types.length === 0) {
                            await fetchFilterOptions();
                          }
                          setShowEditModal(true);
                        }}
                        className={`p-2 rounded ${darkMode ? 'text-blue-400 hover:bg-blue-900/30' : 'text-blue-600 hover:bg-blue-100'}`}
                        title="Manage Stock"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleShowQR(tile)}
                        className={`p-2 rounded ${darkMode ? 'text-purple-400 hover:bg-purple-900/30' : 'text-purple-600 hover:bg-purple-100'}`}
                        title="QR Code"
                      >
                        <FaQrcode className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Cards View Pagination */}
      {viewMode === 'cards' && totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 rounded-l-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              Previous
            </button>
            <span className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300' : 'border-gray-300 bg-gray-50 text-gray-700'}`}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-4 py-2 rounded-r-md border text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600' : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-6xl w-full mx-4 max-h-screen overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tile Management</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setIsEditingTile(false);
                }}
                className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
            
            {/* Debug info - remove in production */}
            <div className={`mb-4 p-2 border rounded text-xs ${darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'}`}>
              <div className="flex items-center justify-between">
                <span>
                  <strong>Debug:</strong> Locations: {locations.length}, Categories: {categories.length}, Types: {types.length}, Brands: {brands.length}, SizesMM: {sizesMM.length}, SizesInches: {sizesInches.length}
                </span>
                <button
                  onClick={fetchFilterOptions}
                  className="ml-2 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                >
                  Reload Data
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Tile Details */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : ''}`}>Tile Information</h3>
                  {!isEditingTile ? (
                    <button
                      onClick={handleEditTile}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center space-x-2"
                    >
                      <FaEdit className="w-3 h-3" />
                      <span>Edit</span>
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveTile}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center space-x-2"
                      >
                        <FaSave className="w-3 h-3" />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tile Name</label>
                    {isEditingTile ? (
                      <input
                        type="text"
                        value={editFormData.tilesName}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, tilesName: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                      />
                    ) : (
                      <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.tilesName}</div>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Part Number</label>
                    {isEditingTile ? (
                      <input
                        type="text"
                        value={editFormData.partNumber}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, partNumber: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        placeholder="Enter part number"
                      />
                    ) : (
                      <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.partNumber || 'Not set'}</div>
                    )}
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Display Name</label>
                    {isEditingTile ? (
                      <input
                        type="text"
                        value={editFormData.displayName}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, displayName: e.target.value }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        placeholder="Enter display name"
                      />
                    ) : (
                      <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.displayName || 'Not set'}</div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                      {isEditingTile ? (
                        <Select
                          value={categories.find(c => c._id === editFormData.tilesCategory) ? 
                            { value: editFormData.tilesCategory, label: categories.find(c => c._id === editFormData.tilesCategory)?.parameterName } : null}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, tilesCategory: option?.value || '' }))}
                          options={categories.map(cat => ({ value: cat._id, label: cat.parameterName }))}
                          isClearable
                          placeholder="Select category"
                          styles={darkMode ? {
                            control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                            singleValue: (base) => ({ ...base, color: '#fff' }),
                            menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                            option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                            input: (base) => ({ ...base, color: '#fff' }),
                            placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                          } : {}}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.tilesCategory?.parameterName}</div>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
                      {isEditingTile ? (
                        <Select
                          value={types.find(t => t._id === editFormData.tilesType) ? 
                            { value: editFormData.tilesType, label: types.find(t => t._id === editFormData.tilesType)?.parameterName } : null}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, tilesType: option?.value || '' }))}
                          options={types.map(type => ({ value: type._id, label: type.parameterName }))}
                          isClearable
                          placeholder="Select type"
                          styles={darkMode ? {
                            control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                            singleValue: (base) => ({ ...base, color: '#fff' }),
                            menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                            option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                            input: (base) => ({ ...base, color: '#fff' }),
                            placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                          } : {}}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.tilesType?.parameterName}</div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (MM)</label>
                      {isEditingTile ? (
                        <Select
                          value={sizesMM.find(s => s._id === editFormData.tilesMM) ? 
                            { value: editFormData.tilesMM, label: sizesMM.find(s => s._id === editFormData.tilesMM)?.parameterName } : null}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, tilesMM: option?.value || '' }))}
                          options={sizesMM.map(size => ({ value: size._id, label: size.parameterName }))}
                          isClearable
                          placeholder="Select size (MM)"
                          styles={darkMode ? {
                            control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                            singleValue: (base) => ({ ...base, color: '#fff' }),
                            menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                            option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                            input: (base) => ({ ...base, color: '#fff' }),
                            placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                          } : {}}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.tilesMM?.parameterName}</div>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (Inches)</label>
                      {isEditingTile ? (
                        <Select
                          value={sizesInches.find(s => s._id === editFormData.tilesInches) ? 
                            { value: editFormData.tilesInches, label: sizesInches.find(s => s._id === editFormData.tilesInches)?.parameterName } : null}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, tilesInches: option?.value || '' }))}
                          options={sizesInches.map(size => ({ value: size._id, label: size.parameterName }))}
                          isClearable
                          placeholder="Select size (Inches)"
                          styles={darkMode ? {
                            control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                            singleValue: (base) => ({ ...base, color: '#fff' }),
                            menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                            option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                            input: (base) => ({ ...base, color: '#fff' }),
                            placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                          } : {}}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.tilesInches?.parameterName}</div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                      {isEditingTile ? (
                        <Select
                          value={brands.find(b => b._id === editFormData.brand) ? 
                            { value: editFormData.brand, label: brands.find(b => b._id === editFormData.brand)?.name } : null}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, brand: option?.value || '' }))}
                          options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
                          isClearable
                          placeholder="Select brand"
                          styles={darkMode ? {
                            control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                            singleValue: (base) => ({ ...base, color: '#fff' }),
                            menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                            option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                            input: (base) => ({ ...base, color: '#fff' }),
                            placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                          } : {}}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>{selectedTile.brand?.name || 'Not set'}</div>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                      {isEditingTile ? (
                        <Select
                          value={{ value: editFormData.status, label: editFormData.status.charAt(0).toUpperCase() + editFormData.status.slice(1) }}
                          onChange={(option) => setEditFormData(prev => ({ ...prev, status: option?.value || 'active' }))}
                          options={[
                            { value: 'active', label: 'Active' },
                            { value: 'inactive', label: 'Inactive' }
                          ]}
                          placeholder="Select status"
                          styles={darkMode ? {
                            control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                            singleValue: (base) => ({ ...base, color: '#fff' }),
                            menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                            option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                            input: (base) => ({ ...base, color: '#fff' }),
                            placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                          } : {}}
                        />
                      ) : (
                        <div className={`p-2 border rounded ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-gray-50'}`}>
                          {selectedTile.status?.charAt(0).toUpperCase() + selectedTile.status?.slice(1) || 'Active'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock Management */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>Stock Management by Location</h3>
                <div className="space-y-4">
                  {locations.length === 0 ? (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <p>No locations available. Please add locations first.</p>
                    </div>
                  ) : (
                    locations.map((location) => {
                      const existingStock = selectedTile.stock?.find(s => s.location?._id === location._id || s.location === location._id);
                      console.log('Location:', location, 'Stock:', selectedTile.stock, 'Existing:', existingStock);
                      return (
                        <div key={location._id} className={`border rounded-lg p-4 ${darkMode ? 'border-gray-600' : ''}`}>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className={`font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{location.name || location.locationName}</h4>
                          <button
                            onClick={() => handleStockEdit(selectedTile, location)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            Edit Stock
                          </button>
                        </div>
                        
                        {existingStock ? (
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center space-x-2">
                              <FaBox className="w-3 h-3 text-green-600" />
                              <span>Fresh: {existingStock.freshStockBox}B, {existingStock.freshStockPieces}P</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <FaExclamationTriangle className="w-3 h-3 text-orange-600" />
                              <span>Damaged: {existingStock.damagedStockBox}B, {existingStock.damagedStockPieces}P</span>
                            </div>
                          </div>
                        ) : (
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No stock recorded</p>
                        )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Edit Modal */}
      {showStockModal && selectedTile && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Edit Stock - {selectedLocation.locationName || selectedLocation.name}
              </h2>
              <button
                onClick={() => setShowStockModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaBox className="w-4 h-4 text-green-600" />
                    <span>Fresh Boxes</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={stockValues.freshStockBox}
                    onChange={(e) => setStockValues(prev => ({ ...prev, freshStockBox: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaCube className="w-4 h-4 text-blue-600" />
                    <span>Fresh Pieces</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={stockValues.freshStockPieces}
                    onChange={(e) => setStockValues(prev => ({ ...prev, freshStockPieces: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaExclamationTriangle className="w-4 h-4 text-orange-600" />
                    <span>Damaged Boxes</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={stockValues.damagedStockBox}
                    onChange={(e) => setStockValues(prev => ({ ...prev, damagedStockBox: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`flex items-center space-x-2 text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <FaBan className="w-4 h-4 text-red-600" />
                    <span>Damaged Pieces</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={stockValues.damagedStockPieces}
                    onChange={(e) => setStockValues(prev => ({ ...prev, damagedStockPieces: parseInt(e.target.value) || 0 }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                  />
                </div>
              </div>

              <div className={`flex justify-end space-x-3 pt-4 border-t ${darkMode ? 'border-gray-700' : ''}`}>
                <button
                  onClick={() => setShowStockModal(false)}
                  className={`px-4 py-2 border rounded-lg ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStockUpdate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                  <FaSave className="w-4 h-4" />
                  <span>Update Stock</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrTile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-xl shadow-2xl max-w-md w-full mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`border-b px-6 py-4 flex justify-between items-center ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <FaQrcode className="text-purple-600" />
                QR Code
              </h3>
              <button
                onClick={() => setShowQRModal(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 text-center space-y-4">
              {/* QR Code Display */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block">
                <QRCode 
                  id="tile-qr-code-svg"
                  value={qrTile._id} 
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>
              
              {/* Tile Info */}
              <div>
                <h4 className={`font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{qrTile.tilesName}</h4>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {qrTile.id}</p>
                {qrTile.tilesMM?.parameterName && (
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{qrTile.tilesMM.parameterName}</p>
                )}
              </div>

              {/* Export Options */}
              <div className={`space-y-3 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Export as:</p>
                
                {/* Image formats row */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={downloadQRAsPNG}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <FaFileImage className="w-4 h-4" />
                    PNG
                  </button>
                  <button
                    onClick={downloadQRAsJPG}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <FaFileImage className="w-4 h-4" />
                    JPG
                  </button>
                  <button
                    onClick={downloadQRAsSVG}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <FaFileImage className="w-4 h-4" />
                    SVG
                  </button>
                </div>

                {/* PDF formats row */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => downloadQRAsPDF(false)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <FaFilePdf className="w-4 h-4" />
                    PDF (QR Only)
                  </button>
                  <button
                    onClick={() => downloadQRAsPDF(true)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <FaFilePdf className="w-4 h-4" />
                    PDF with Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk QR Export Modal */}
      {showBulkQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`border-b px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <FaQrcode className="text-purple-600" />
                Export QR Codes (Bulk)
              </h3>
              <button
                onClick={() => setShowBulkQRModal(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Select filters to generate QR codes for matching tiles. Leave filters empty to include all tiles.
              </p>
              
              {/* Filters Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {/* Brand Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                  <Select
                    placeholder="All Brands"
                    isClearable
                    value={bulkQRFilters.brand ? { value: bulkQRFilters.brand, label: brands.find(b => b._id === bulkQRFilters.brand)?.name } : null}
                    onChange={(option) => handleBulkQRFilterChange('brand', option?.value || null)}
                    options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>

                {/* Category Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <Select
                    placeholder="All Categories"
                    isClearable
                    value={bulkQRFilters.category ? { value: bulkQRFilters.category, label: categories.find(c => c._id === bulkQRFilters.category)?.parameterName } : null}
                    onChange={(option) => handleBulkQRFilterChange('category', option?.value || null)}
                    options={categories.map(cat => ({ value: cat._id, label: cat.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>

                {/* Type Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
                  <Select
                    placeholder="All Types"
                    isClearable
                    value={bulkQRFilters.type ? { value: bulkQRFilters.type, label: types.find(t => t._id === bulkQRFilters.type)?.parameterName } : null}
                    onChange={(option) => handleBulkQRFilterChange('type', option?.value || null)}
                    options={types.map(type => ({ value: type._id, label: type.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>

                {/* Finish Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Finish</label>
                  <Select
                    placeholder="All Finishes"
                    isClearable
                    value={bulkQRFilters.finish ? { value: bulkQRFilters.finish, label: finishes.find(f => f._id === bulkQRFilters.finish)?.parameterName } : null}
                    onChange={(option) => handleBulkQRFilterChange('finish', option?.value || null)}
                    options={finishes.map(finish => ({ value: finish._id, label: finish.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>

                {/* Size MM Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (MM)</label>
                  <Select
                    placeholder="All Sizes"
                    isClearable
                    value={bulkQRFilters.sizeMM ? { value: bulkQRFilters.sizeMM, label: sizesMM.find(s => s._id === bulkQRFilters.sizeMM)?.parameterName } : null}
                    onChange={(option) => handleBulkQRFilterChange('sizeMM', option?.value || null)}
                    options={sizesMM.map(size => ({ value: size._id, label: size.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>

                {/* Size Inches Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (Inches)</label>
                  <Select
                    placeholder="All Sizes"
                    isClearable
                    value={bulkQRFilters.sizeInches ? { value: bulkQRFilters.sizeInches, label: sizesInches.find(s => s._id === bulkQRFilters.sizeInches)?.parameterName } : null}
                    onChange={(option) => handleBulkQRFilterChange('sizeInches', option?.value || null)}
                    options={sizesInches.map(size => ({ value: size._id, label: size.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <Select
                    placeholder="All Statuses"
                    isClearable
                    value={bulkQRFilters.status ? { value: bulkQRFilters.status, label: bulkQRFilters.status.charAt(0).toUpperCase() + bulkQRFilters.status.slice(1) } : null}
                    onChange={(option) => handleBulkQRFilterChange('status', option?.value || null)}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' }
                    ]}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
              </div>

              {/* Results Summary */}
              <div className={`rounded-lg p-4 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Matching tiles: </span>
                    <span className="font-bold text-lg text-blue-600">
                      {loadingBulkQR ? (
                        <span className="inline-flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          Loading...
                        </span>
                      ) : (
                        bulkQRTiles.length
                      )}
                    </span>
                  </div>
                  {bulkQRTiles.length > 0 && (
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {Math.ceil(bulkQRTiles.length / 28)} page(s) of QR labels
                    </span>
                  )}
                </div>
              </div>

              {/* Preview of matching tiles */}
              {bulkQRTiles.length > 0 && (
                <div className={`border rounded-lg max-h-40 overflow-y-auto ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <div className={`p-2 border-b sticky top-0 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview (first 10 tiles)</span>
                  </div>
                  <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                    {bulkQRTiles.slice(0, 10).map((tile) => (
                      <div key={tile._id} className="px-3 py-2 text-sm flex justify-between items-center">
                        <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{tile.tilesName}</span>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{tile.id}</span>
                      </div>
                    ))}
                    {bulkQRTiles.length > 10 && (
                      <div className={`px-3 py-2 text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        ... and {bulkQRTiles.length - 10} more tiles
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`border-t px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => {
                  setBulkQRFilters({
                    brand: null,
                    category: null,
                    type: null,
                    finish: null,
                    sizeMM: null,
                    sizeInches: null,
                    status: 'active'
                  });
                }}
                className={`px-4 py-2 flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
              >
                <FaTimes className="w-4 h-4" />
                <span>Clear Filters</span>
              </button>
              <button
                onClick={generateBulkQRZip}
                disabled={bulkQRTiles.length === 0 || exportLoading}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <FaQrcode className="w-4 h-4" />
                    <span>Generate {bulkQRTiles.length} QR Codes (ZIP)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Stock Modal */}
      {showExportStockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`border-b px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <FaFileExport className="text-green-600" />
                Export Stock to Excel
              </h3>
              <button
                onClick={() => setShowExportStockModal(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-grow">
              <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Select filters to export stock data. Leave filters empty to export all tiles.
              </p>
              
              {/* Filters Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                  <Select
                    placeholder="All Brands"
                    isClearable
                    value={exportStockFilters.brand ? { value: exportStockFilters.brand, label: brands.find(b => b._id === exportStockFilters.brand)?.name } : null}
                    onChange={(option) => handleExportStockFilterChange('brand', option?.value || null)}
                    options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <Select
                    placeholder="All Categories"
                    isClearable
                    value={exportStockFilters.category ? { value: exportStockFilters.category, label: categories.find(c => c._id === exportStockFilters.category)?.parameterName } : null}
                    onChange={(option) => handleExportStockFilterChange('category', option?.value || null)}
                    options={categories.map(cat => ({ value: cat._id, label: cat.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
                  <Select
                    placeholder="All Types"
                    isClearable
                    value={exportStockFilters.type ? { value: exportStockFilters.type, label: types.find(t => t._id === exportStockFilters.type)?.parameterName } : null}
                    onChange={(option) => handleExportStockFilterChange('type', option?.value || null)}
                    options={types.map(type => ({ value: type._id, label: type.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Finish</label>
                  <Select
                    placeholder="All Finishes"
                    isClearable
                    value={exportStockFilters.finish ? { value: exportStockFilters.finish, label: finishes.find(f => f._id === exportStockFilters.finish)?.parameterName } : null}
                    onChange={(option) => handleExportStockFilterChange('finish', option?.value || null)}
                    options={finishes.map(finish => ({ value: finish._id, label: finish.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (MM)</label>
                  <Select
                    placeholder="All Sizes"
                    isClearable
                    value={exportStockFilters.sizeMM ? { value: exportStockFilters.sizeMM, label: sizesMM.find(s => s._id === exportStockFilters.sizeMM)?.parameterName } : null}
                    onChange={(option) => handleExportStockFilterChange('sizeMM', option?.value || null)}
                    options={sizesMM.map(size => ({ value: size._id, label: size.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (Inches)</label>
                  <Select
                    placeholder="All Sizes"
                    isClearable
                    value={exportStockFilters.sizeInches ? { value: exportStockFilters.sizeInches, label: sizesInches.find(s => s._id === exportStockFilters.sizeInches)?.parameterName } : null}
                    onChange={(option) => handleExportStockFilterChange('sizeInches', option?.value || null)}
                    options={sizesInches.map(size => ({ value: size._id, label: size.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <Select
                    placeholder="All Statuses"
                    isClearable
                    value={exportStockFilters.status ? { value: exportStockFilters.status, label: exportStockFilters.status.charAt(0).toUpperCase() + exportStockFilters.status.slice(1) } : null}
                    onChange={(option) => handleExportStockFilterChange('status', option?.value || null)}
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' }
                    ]}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location (Optional)</label>
                  <Select
                    placeholder="All Locations"
                    isClearable
                    value={exportStockFilters.location ? { value: exportStockFilters.location, label: locations.find(l => l._id === exportStockFilters.location)?.name || locations.find(l => l._id === exportStockFilters.location)?.locationName } : null}
                    onChange={(option) => handleExportStockFilterChange('location', option?.value || null)}
                    options={locations.map(loc => ({ value: loc._id, label: loc.name || loc.locationName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`border-t px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => {
                  setExportStockFilters({
                    brand: null,
                    category: null,
                    type: null,
                    finish: null,
                    sizeMM: null,
                    sizeInches: null,
                    status: 'active',
                    location: null
                  });
                }}
                className={`px-4 py-2 flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
              >
                <FaTimes className="w-4 h-4" />
                <span>Clear Filters</span>
              </button>
              <button
                onClick={handleExportStock}
                disabled={exportLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <FaFileExcel className="w-4 h-4" />
                    <span>Export Excel</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Movement Export Modal */}
      {showStockMovementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b flex items-center justify-between flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-green-50'}`}>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaCalendarAlt className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Export Stock Movement</h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Select date range, location and optional filters</p>
                </div>
              </div>
              <button onClick={() => setShowStockMovementModal(false)} className={`p-2 rounded-full hover:bg-opacity-20 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-400 hover:bg-gray-100'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-4 overflow-y-auto flex-grow">
              {/* Required Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Start Date</label>
                  <input
                    type="date"
                    value={stockMovementFilters.startDate}
                    onChange={(e) => setStockMovementFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>End Date</label>
                  <input
                    type="date"
                    value={stockMovementFilters.endDate}
                    onChange={(e) => setStockMovementFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus:ring-green-500 focus:border-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Location <span className="text-red-500">*</span></label>
                  <Select
                    placeholder="Select Location"
                    value={stockMovementFilters.location ? { value: stockMovementFilters.location, label: locations.find(l => l._id === stockMovementFilters.location)?.locationName || locations.find(l => l._id === stockMovementFilters.location)?.name } : null}
                    onChange={(option) => setStockMovementFilters(prev => ({ ...prev, location: option?.value || '' }))}
                    options={locations.map(loc => ({ value: loc._id, label: loc.locationName || loc.name }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
              </div>

              {/* Optional Filters */}
              <div>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Optional Filters</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                    <Select
                      placeholder="All Brands"
                      isClearable
                      value={stockMovementFilters.brand ? { value: stockMovementFilters.brand, label: brands.find(b => b._id === stockMovementFilters.brand)?.name } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, brand: option?.value || null }))}
                      options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
                      styles={darkMode ? {
                        control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                        singleValue: (base) => ({ ...base, color: '#fff' }),
                        menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                        input: (base) => ({ ...base, color: '#fff' }),
                        placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                      } : {}}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                    <Select
                      placeholder="All Categories"
                      isClearable
                      value={stockMovementFilters.category ? { value: stockMovementFilters.category, label: categories.find(c => c._id === stockMovementFilters.category)?.parameterName } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, category: option?.value || null }))}
                      options={categories.map(cat => ({ value: cat._id, label: cat.parameterName }))}
                      styles={darkMode ? {
                        control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                        singleValue: (base) => ({ ...base, color: '#fff' }),
                        menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                        input: (base) => ({ ...base, color: '#fff' }),
                        placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                      } : {}}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
                    <Select
                      placeholder="All Types"
                      isClearable
                      value={stockMovementFilters.type ? { value: stockMovementFilters.type, label: types.find(t => t._id === stockMovementFilters.type)?.parameterName } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, type: option?.value || null }))}
                      options={types.map(type => ({ value: type._id, label: type.parameterName }))}
                      styles={darkMode ? {
                        control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                        singleValue: (base) => ({ ...base, color: '#fff' }),
                        menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                        input: (base) => ({ ...base, color: '#fff' }),
                        placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                      } : {}}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Finish</label>
                    <Select
                      placeholder="All Finishes"
                      isClearable
                      value={stockMovementFilters.finish ? { value: stockMovementFilters.finish, label: finishes.find(f => f._id === stockMovementFilters.finish)?.parameterName } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, finish: option?.value || null }))}
                      options={finishes.map(finish => ({ value: finish._id, label: finish.parameterName }))}
                      styles={darkMode ? {
                        control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                        singleValue: (base) => ({ ...base, color: '#fff' }),
                        menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                        input: (base) => ({ ...base, color: '#fff' }),
                        placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                      } : {}}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (MM)</label>
                    <Select
                      placeholder="All Sizes"
                      isClearable
                      value={stockMovementFilters.sizeMM ? { value: stockMovementFilters.sizeMM, label: sizesMM.find(s => s._id === stockMovementFilters.sizeMM)?.parameterName } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, sizeMM: option?.value || null }))}
                      options={sizesMM.map(size => ({ value: size._id, label: size.parameterName }))}
                      styles={darkMode ? {
                        control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                        singleValue: (base) => ({ ...base, color: '#fff' }),
                        menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                        input: (base) => ({ ...base, color: '#fff' }),
                        placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                      } : {}}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (Inches)</label>
                    <Select
                      placeholder="All Sizes"
                      isClearable
                      value={stockMovementFilters.sizeInches ? { value: stockMovementFilters.sizeInches, label: sizesInches.find(s => s._id === stockMovementFilters.sizeInches)?.parameterName } : null}
                      onChange={(option) => setStockMovementFilters(prev => ({ ...prev, sizeInches: option?.value || null }))}
                      options={sizesInches.map(size => ({ value: size._id, label: size.parameterName }))}
                      styles={darkMode ? {
                        control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', color: '#fff' }),
                        singleValue: (base) => ({ ...base, color: '#fff' }),
                        menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                        input: (base) => ({ ...base, color: '#fff' }),
                        placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                      } : {}}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`border-t px-6 py-4 flex justify-end space-x-3 flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => setShowStockMovementModal(false)}
                className={`px-4 py-2 rounded-lg ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleStockMovementExport}
                disabled={stockMovementLoading || !stockMovementFilters.startDate || !stockMovementFilters.endDate || !stockMovementFilters.location}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {stockMovementLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <FaFileExcel className="w-4 h-4" />
                    <span>Export Excel</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Weight Update Modal */}
      {showBulkWeightModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`px-6 py-4 border-b flex items-center justify-between flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-yellow-50'}`}>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FaWeight className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Bulk Weight Update</h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Update weight per box (kg) for tiles</p>
                </div>
              </div>
              <button onClick={() => setShowBulkWeightModal(false)} className={`p-2 rounded-full hover:bg-opacity-20 ${darkMode ? 'text-gray-400 hover:bg-gray-600' : 'text-gray-400 hover:bg-gray-100'}`}>
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Filters */}
            <div className={`px-6 py-4 border-b flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Brand</label>
                  <Select
                    placeholder="All Brands"
                    isClearable
                    value={bulkWeightFilters.brand ? { value: bulkWeightFilters.brand, label: brands.find(b => b._id === bulkWeightFilters.brand)?.name } : null}
                    onChange={(option) => setBulkWeightFilters(prev => ({ ...prev, brand: option?.value || null }))}
                    options={brands.map(b => ({ value: b._id, label: b.name }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', minHeight: '36px' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                  <Select
                    placeholder="All Categories"
                    isClearable
                    value={bulkWeightFilters.category ? { value: bulkWeightFilters.category, label: categories.find(c => c._id === bulkWeightFilters.category)?.parameterName } : null}
                    onChange={(option) => setBulkWeightFilters(prev => ({ ...prev, category: option?.value || null }))}
                    options={categories.map(cat => ({ value: cat._id, label: cat.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', minHeight: '36px' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
                  <Select
                    placeholder="All Types"
                    isClearable
                    value={bulkWeightFilters.type ? { value: bulkWeightFilters.type, label: types.find(t => t._id === bulkWeightFilters.type)?.parameterName } : null}
                    onChange={(option) => setBulkWeightFilters(prev => ({ ...prev, type: option?.value || null }))}
                    options={types.map(type => ({ value: type._id, label: type.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', minHeight: '36px' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Size (MM)</label>
                  <Select
                    placeholder="All Sizes"
                    isClearable
                    value={bulkWeightFilters.sizeMM ? { value: bulkWeightFilters.sizeMM, label: sizesMM.find(s => s._id === bulkWeightFilters.sizeMM)?.parameterName } : null}
                    onChange={(option) => setBulkWeightFilters(prev => ({ ...prev, sizeMM: option?.value || null }))}
                    options={sizesMM.map(size => ({ value: size._id, label: size.parameterName }))}
                    styles={darkMode ? {
                      control: (base) => ({ ...base, backgroundColor: '#374151', borderColor: '#4B5563', minHeight: '36px' }),
                      singleValue: (base) => ({ ...base, color: '#fff' }),
                      menu: (base) => ({ ...base, backgroundColor: '#374151' }),
                      option: (base, state) => ({ ...base, backgroundColor: state.isFocused ? '#4B5563' : '#374151', color: '#fff' }),
                      input: (base) => ({ ...base, color: '#fff' }),
                      placeholder: (base) => ({ ...base, color: '#9CA3AF' })
                    } : {}}
                  />
                </div>
              </div>
            </div>

            {/* Results count */}
            <div className={`px-6 py-2 border-b flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {bulkWeightLoading ? 'Loading...' : `${bulkWeightTiles.length} tiles found`}
                </span>
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {Object.keys(bulkWeightValues).filter(k => bulkWeightValues[k] !== '' && bulkWeightValues[k] !== null && bulkWeightValues[k] !== undefined).length} weights set
                </span>
              </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-y-auto px-6 py-2">
              {bulkWeightLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
                </div>
              ) : bulkWeightTiles.length === 0 ? (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <FaWeight className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No tiles found. Adjust filters to see tiles.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className={`sticky top-0 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <tr className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <th className={`text-left py-2 pr-2 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Tile ID</th>
                      <th className={`text-left py-2 pr-2 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Name</th>
                      <th className={`text-left py-2 pr-2 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Size</th>
                      <th className={`text-right py-2 pr-2 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pcs/Box</th>
                      <th className={`text-right py-2 w-32 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Weight (kg/box)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkWeightTiles.map((tile) => (
                      <tr key={tile._id} className={`border-b ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={`py-2 pr-2 font-mono text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tile.id}</td>
                        <td className={`py-2 pr-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{tile.tilesName}</td>
                        <td className={`py-2 pr-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tile.tilesMM?.parameterName || '-'}</td>
                        <td className={`py-2 pr-2 text-right ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{tile.tilesPiecesPerBox || '-'}</td>
                        <td className="py-2 text-right">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            placeholder="0.0"
                            value={bulkWeightValues[tile._id] ?? tile.weight ?? ''}
                            onChange={(e) => setBulkWeightValues(prev => ({ ...prev, [tile._id]: e.target.value }))}
                            className={`w-full px-2 py-1 text-right rounded border focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div className={`border-t px-6 py-4 flex justify-between items-center flex-shrink-0 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={() => {
                  setBulkWeightFilters({ brand: null, category: null, type: null, sizeMM: null });
                  setBulkWeightValues({});
                }}
                className={`px-4 py-2 flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
              >
                <FaTimes className="w-4 h-4" />
                <span>Clear All</span>
              </button>
              <button
                onClick={handleBulkWeightSave}
                disabled={bulkWeightLoading || Object.keys(bulkWeightValues).length === 0}
                className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkWeightLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    <span>Save Weights</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TilesManagement;